/**
 * Mobile Calendar Component
 * Calendar view for upcoming performances
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useMobile } from '../contexts/MobileContext';
import MobileCard from './MobileCard';
import MobileBottomSheet from './MobileBottomSheet';
import MobileDateTimePicker from './MobileDateTimePicker';
import MobileFormInput from './MobileFormInput';
import MobileEmptyState from './MobileEmptyState';
import { ThemedButton } from './ui/ThemedButton';
import { formatGigTime12h } from '@/lib/gigUtils';

const MobileCalendar = ({
  performances = [],
  onAddPerformance,
  onPerformanceClick,
  className = '',
}) => {
  const mobile = useMobile();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'list'

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getPerformancesForDate = (date) => {
    if (!date) return [];
    return performances.filter(p => {
      const perfDate = p.date?.toDate ? p.date.toDate() : new Date(p.date);
      return perfDate.toDateString() === date.toDateString();
    });
  };

  const navigateMonth = (direction) => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  if (!mobile.isMobile) {
    return (
      <div className={className}>
        <h2 className="text-xl font-semibold mb-4">Upcoming Performances</h2>
        <div className="space-y-2">
          {performances.map((perf, index) => (
            <div key={index} className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium">{perf.venue}</p>
              <p className="text-sm text-gray-600">{perf.date}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const days = getDaysInMonth(selectedDate);
  const monthName = selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const todayPerformances = getPerformancesForDate(new Date());

  return (
    <div className={className}>
      <MobileCard
        title="Performance Calendar"
        icon="📅"
        variant="elevated"
      >
        {/* View Toggle */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1 rounded-lg text-xs font-medium ${
                viewMode === 'month' ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded-lg text-xs font-medium ${
                viewMode === 'list' ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400'
              }`}
            >
              List
            </button>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="p-2 bg-purple-600 rounded-lg"
          >
            <Plus className="w-4 h-4 text-white" />
          </button>
        </div>

        {viewMode === 'month' ? (
          <>
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 text-gray-400"
              >
                ←
              </button>
              <h3 className="text-sm font-semibold text-white">{monthName}</h3>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 text-gray-400"
              >
                →
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs text-gray-400 p-1">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                const dayPerformances = day ? getPerformancesForDate(day) : [];
                const isToday = day && day.toDateString() === new Date().toDateString();
                const isSelected = day && day.toDateString() === selectedDate.toDateString();

                return (
                  <button
                    key={index}
                    onClick={() => day && setSelectedDate(day)}
                    className={`aspect-square p-1 text-xs rounded ${
                      !day
                        ? 'invisible'
                        : isToday
                        ? 'bg-purple-600 text-white font-bold'
                        : isSelected
                        ? 'bg-purple-900/50 text-white'
                        : dayPerformances.length > 0
                        ? 'bg-green-900/30 text-green-400'
                        : 'bg-gray-800/30 text-gray-400 hover:bg-gray-800/50'
                    }`}
                  >
                    <div>{day ? day.getDate() : ''}</div>
                    {dayPerformances.length > 0 && (
                      <div className="w-1 h-1 bg-green-400 rounded-full mx-auto mt-0.5" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected Date Performances */}
            {getPerformancesForDate(selectedDate).length > 0 && (
              <div className="mt-4 space-y-2">
                <h4 className="text-sm font-semibold text-white mb-2">
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </h4>
                {getPerformancesForDate(selectedDate).map((perf, index) => (
                  <motion.div
                    key={index}
                    onClick={() => onPerformanceClick && onPerformanceClick(perf)}
                    className="p-3 bg-gray-800/50 rounded-lg cursor-pointer"
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <MapPin className="w-4 h-4 text-purple-400" />
                      <p className="font-medium text-white">{perf.venue}</p>
                    </div>
                    {perf.time && (
                      <div className="flex items-center space-x-2">
                        <Clock className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-400">{formatGigTime12h(perf.time)}</span>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* List View */
          <div className="space-y-2">
            {performances.length === 0 ? (
              <MobileEmptyState
                icon="📅"
                title="No performances scheduled"
                description="Add performances to see them here"
                actionLabel="Add Performance"
                onAction={() => setShowAddModal(true)}
              />
            ) : (
              performances.map((perf, index) => (
                <motion.div
                  key={index}
                  onClick={() => onPerformanceClick && onPerformanceClick(perf)}
                  className="p-3 bg-gray-800/50 rounded-lg cursor-pointer"
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <MapPin className="w-4 h-4 text-purple-400" />
                        <p className="font-medium text-white">{perf.venue}</p>
                      </div>
                      <div className="flex items-center space-x-3 text-xs text-gray-400">
                        {perf.date && (
                          <span>{new Date(perf.date).toLocaleDateString()}</span>
                        )}
                        {perf.time && (
                          <span>{formatGigTime12h(perf.time)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}
      </MobileCard>

      {/* Add Performance Modal */}
      <MobileBottomSheet
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Performance"
      >
        <AddPerformanceForm
          onSave={(data) => {
            if (onAddPerformance) onAddPerformance(data);
            setShowAddModal(false);
          }}
          onCancel={() => setShowAddModal(false)}
        />
      </MobileBottomSheet>
    </div>
  );
};

// Separate form component
const AddPerformanceForm = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    venue: '',
    date: '',
    time: '',
  });

  return (
    <div className="space-y-4">
      <MobileFormInput
        type="text"
        label="Venue"
        value={formData.venue}
        onChange={(e) => setFormData(prev => ({ ...prev, venue: e.target.value }))}
        placeholder="Enter venue name"
      />
      <MobileDateTimePicker
        type="date"
        label="Date"
        value={formData.date}
        onChange={(value) => setFormData(prev => ({ ...prev, date: value }))}
      />
      <MobileDateTimePicker
        type="time"
        label="Time"
        value={formData.time}
        onChange={(value) => setFormData(prev => ({ ...prev, time: value }))}
      />
      <div className="flex space-x-2">
        <ThemedButton
          variant="secondary"
          size="md"
          onClick={onCancel}
          className="flex-1"
        >
          Cancel
        </ThemedButton>
        <ThemedButton
          variant="primary"
          size="md"
          onClick={() => onSave(formData)}
          className="flex-1"
          disabled={!formData.venue.trim()}
        >
          Add Performance
        </ThemedButton>
      </div>
    </div>
  );
};

export default MobileCalendar;
